import { Link } from 'react-router-dom'
import { Faq } from '../components/Faq'
import { useReveal } from '../components/marketing/motion'
import faqs from '../data/faqs.json'
import { usePageMeta } from '../lib/usePageMeta'

const MCP_FAQ = faqs.mcp

const CONFIG = `{
  "mcpServers": {
    "ocean-schedules": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "https://mcp.schedulesmcp.com/mcp",
               "--header", "Authorization: Bearer YOUR_API_KEY"]
    }
  }
}`

const TOOLS = [
  {
    name: 'compare_lanes',
    desc: 'Rank carriers on an origin→destination pair by on-time reliability and transit time.',
  },
  {
    name: 'carrier_reliability',
    desc: 'On-time % and delay distribution for a carrier, optionally per lane.',
  },
  { name: 'transit_time', desc: 'Observed median / p90 transit time for a carrier on a lane.' },
  { name: 'find_sailings', desc: 'Observed and derived upcoming departures on a lane.' },
  {
    name: 'get_schedules',
    desc: 'Published forward sailings on a lane: carrier, vessel, voyage, ETD/ETA and cut-offs.',
  },
  {
    name: 'find_best_sailings',
    desc: 'Best upcoming sailings on a lane, one per carrier, ranked. Filter by dates, transit, reliability, open cut-offs.',
  },
  {
    name: 'watch_lane',
    desc: 'Watch a lane for schedule alerts: blank sailings, closing cut-offs, slips, reliability drops (paid).',
  },
  { name: 'port_reliability', desc: 'Congestion and dwell signals for a port.' },
  {
    name: 'carrier_league',
    desc: 'League table of carriers ranked by overall on-time reliability.',
  },
  {
    name: 'port_congestion',
    desc: 'Fused port-congestion signal: level, expected added wait in days, and confidence.',
  },
  {
    name: 'active_cyclones',
    desc: 'Live tropical cyclones currently active on ocean trade lanes.',
  },
  {
    name: 'lane_storm_exposure',
    desc: "Whether a lane currently passes through an active cyclone's forecast gale field.",
  },
]

export function McpPage() {
  usePageMeta(
    'MCP server · SchedulesMCP',
    'Use SchedulesMCP from your AI client over MCP. Compare ocean carriers and estimate transit right in your workflow. Remote Streamable HTTP, public demo key.'
  )
  useReveal('main')
  return (
    <main className="max-w-6xl mx-auto px-6 pt-12 pb-10">
      <p
        className="m-0 font-mono text-xs uppercase text-accent text-center"
        style={{ letterSpacing: '0.14em' }}
      >
        For AI agents
      </p>
      <h1
        className="font-display text-4xl mb-3 mt-3 text-center"
        style={{ fontWeight: 210, letterSpacing: '-0.03em' }}
      >
        MCP server
      </h1>
      <p className="text-[var(--text-secondary)] mb-8 max-w-2xl text-center mx-auto">
        Ask your AI client to compare carriers and estimate transit right in your workflow. Remote
        Streamable HTTP at <code className="font-mono text-signal">mcp.schedulesmcp.com/mcp</code>{' '}
        (point your MCP client at it; it isn't a page you open in a browser). Use the public demo
        key to try it with no signup. It's the same lane and carrier reliability you see on Compare.
        Compare a lane to see it, subscribe for full coverage and your own key, then pull the answer
        into Claude, Cursor, or Windsurf when you're deciding who to book.
      </p>

      <h2 className="font-display font-light text-xl mb-3 tracking-[-0.015em]">
        Connect (Claude Desktop)
      </h2>
      <pre className="bg-surface-0 border border-border rounded-xl p-4 overflow-x-auto text-sm font-mono text-[var(--text-secondary)] mb-3 leading-relaxed">
        {CONFIG}
      </pre>
      <p className="text-sm text-[var(--text-muted)] mb-10">
        Replace <span className="font-mono">YOUR_API_KEY</span> with a key from your dashboard, or
        use <span className="font-mono text-signal">smcp_demo_public</span>, a sample-data key for
        testing your connection. The lane search on the{' '}
        <Link to="/compare" className="text-signal underline underline-offset-2">
          Compare page
        </Link>{' '}
        is live real data; your own key gives the same live coverage over MCP. Cursor / Windsurf
        support the <span className="font-mono">url</span> form natively.
      </p>

      <h2 className="font-display font-light text-xl mb-4 tracking-[-0.015em]">Tools</h2>
      <div className="space-y-3">
        {TOOLS.map((t) => (
          <div key={t.name} className="bg-surface-0 border border-border rounded-xl p-4">
            <code className="font-mono text-signal">{t.name}</code>
            <p className="text-sm text-[var(--text-secondary)] mt-1">{t.desc}</p>
          </div>
        ))}
      </div>

      <div className="mt-12 flex flex-wrap items-center gap-x-5 gap-y-3 border-t border-border pt-8">
        <Link
          to="/pricing"
          className="bg-signal text-on-accent font-bold px-5 py-2 rounded-lg no-underline hover:opacity-90"
        >
          Get your keys
        </Link>
        <Link to="/compare" className="text-sm text-signal no-underline hover:underline">
          Or compare a lane first →
        </Link>
      </div>

      <Faq id="mcp" items={MCP_FAQ} heading="MCP server: FAQ" />
    </main>
  )
}
